import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MatrixLink from '../ui/MatrixLink';
import { getBackdropUrl, getImageUrl } from '../../services/tmdb';
import { getChimeraScan } from '../../services/chimeraLens';
import { useStore } from '../../store/useStore';
import type { Movie, TvShow } from '../../types';
import { getMediaTitle } from '../../types';

interface HeroBannerProps {
  media: Movie | TvShow | null;
  loading?: boolean;
  /**
   * Optional pool of featured titles. When provided (and >1 entry), the hero
   * cross-fades through the pool every ROTATION_MS, pausing on hover and
   * honoring prefers-reduced-motion. Falls back to `media` when absent.
   */
  heroPool?: (Movie | TvShow)[];
}

const ROTATION_MS = 7000;
const PARALLAX_PX = 8;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export default function HeroBanner({ media, loading = false, heroPool }: HeroBannerProps) {
  const navigate = useNavigate();
  const { addToWatchlist, removeFromWatchlist, isInWatchlist, setTrailerKey } = useStore();

  // --- Featured rotation ----------------------------------------------------
  // The pool drives which title is "active". When no usable pool is passed we
  // fall back to a single-entry pool built from the `media` prop so the rest of
  // the component can treat both cases identically.
  const pool = useMemo<(Movie | TvShow)[]>(() => {
    const candidates = heroPool && heroPool.length > 0 ? heroPool : media ? [media] : [];
    // De-dupe by id so the cross-fade never lands on the same backdrop twice.
    const seen = new Set<number>();
    return candidates.filter((item) => {
      if (!item || seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [heroPool, media]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [reducedMotion, setReducedMotion] = useState<boolean>(prefersReducedMotion);

  // Keep activeIndex in range whenever the pool changes (e.g. data arrives).
  useEffect(() => {
    setActiveIndex((i) => (pool.length === 0 ? 0 : i % pool.length));
  }, [pool.length]);

  // React to prefers-reduced-motion changes at runtime.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReducedMotion(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  // 7s cross-fade rotation — paused on hover, skipped under reduced motion.
  useEffect(() => {
    if (reducedMotion || isHovered || pool.length <= 1) return;
    const id = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % pool.length);
    }, ROTATION_MS);
    return () => window.clearInterval(id);
  }, [reducedMotion, isHovered, pool.length]);

  // The active media is derived from the pool, falling back to the prop.
  const activeMedia = pool[activeIndex] ?? media;

  // --- Per-active-media derivations ----------------------------------------
  const isMovie = activeMedia && 'title' in activeMedia;
  const mediaType = isMovie ? 'movie' : 'tv';
  const title = activeMedia ? getMediaTitle(activeMedia) : '';
  const overview = activeMedia?.overview || '';
  const year = activeMedia
    ? new Date(isMovie ? activeMedia.release_date : (activeMedia as TvShow).first_air_date).getFullYear()
    : '';
  const rating = activeMedia?.vote_average?.toFixed(1) || '';
  const inWatchlist = activeMedia ? isInWatchlist(mediaType, activeMedia.id) : false;

  // Get trailer key
  const trailerKey = activeMedia?.videos?.results?.find(
    (v) => v.type === 'Trailer' && v.site === 'YouTube'
  )?.key;

  // Synchronous scan signal — substrate-anchor visible at the hero before any
  // click. Falls back gracefully even when no AI key is configured.
  const scanSignal = useMemo(() => {
    if (!activeMedia) return null;
    return getChimeraScan(activeMedia, mediaType).signal;
  }, [activeMedia, mediaType]);

  // --- Backdrop cross-fade --------------------------------------------------
  // Each backdrop layer fades in when its index matches activeIndex, giving the
  // 7s cross-fade between the trending heroes already fetched.
  const [loadedKeys, setLoadedKeys] = useState<Record<number, boolean>>({});
  const markLoaded = useCallback((key: number) => {
    setLoadedKeys((prev) => ({ ...prev, [key]: true }));
  }, []);
  // Reset load tracking when the pool identity changes.
  useEffect(() => {
    setLoadedKeys({});
  }, [pool]);

  // --- Mouse parallax (matches StarfieldCanvas: depth-weighted, lerped) ------
  const backdropRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number>(0);
  // Smoothed normalized pointer offset, -1..1 on each axis.
  const target = useRef({ x: 0, y: 0 });
  const current = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (reducedMotion) {
      if (backdropRef.current) backdropRef.current.style.transform = '';
      return;
    }
    const el = backdropRef.current;
    if (!el) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Normalize pointer position to -1..1 relative to viewport center.
      target.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      target.current.y = (e.clientY / window.innerHeight) * 2 - 1;
    };

    const renderLoop = () => {
      // Organic lerp for inertia, mirroring StarfieldCanvas smoothing.
      current.current.x += (target.current.x - current.current.x) * 0.06;
      current.current.y += (target.current.y - current.current.y) * 0.06;
      const px = current.current.x * PARALLAX_PX;
      const py = current.current.y * PARALLAX_PX;
      el.style.transform = `translate3d(${px}px, ${py}px, 0)`;
      frameRef.current = requestAnimationFrame(renderLoop);
    };

    window.addEventListener('mousemove', handleMouseMove);
    frameRef.current = requestAnimationFrame(renderLoop);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(frameRef.current);
    };
  }, [reducedMotion]);

  const handleWatchlist = () => {
    if (!activeMedia) return;
    if (inWatchlist) {
      removeFromWatchlist(mediaType, activeMedia.id);
    } else {
      addToWatchlist({
        id: activeMedia.id,
        mediaType,
        title,
        posterPath: activeMedia.poster_path,
        backdropPath: activeMedia.backdrop_path,
        voteAverage: activeMedia.vote_average,
        releaseDate: isMovie ? activeMedia.release_date : (activeMedia as TvShow).first_air_date,
        addedAt: Date.now(),
      });
    }
  };

  if (loading || !activeMedia) {
    return (
      <div className="relative h-[65vh] sm:h-[75vh] lg:h-[85vh] shimmer">
        <div className="absolute bottom-0 left-0 right-0 h-1/2 gradient-overlay" />
      </div>
    );
  }

  return (
    <div
      className="relative h-[65vh] sm:h-[75vh] lg:h-[85vh] overflow-hidden vignette"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background Image — Ken-Burns drift + mouse parallax + 7s cross-fade.
          The parallax transform lives on this wrapper; the ken-burns scale
          lives on each <img> so the two transforms don't fight. A slight
          over-scale (110%) hides the parallax translate edges. */}
      <div
        ref={backdropRef}
        className="absolute inset-0 will-change-transform"
        style={{ scale: '1.06' }}
      >
        {pool.map((item, idx) => {
          const isActive = idx === activeIndex;
          const isLoaded = loadedKeys[item.id];
          return (
            <img
              key={item.id}
              src={getBackdropUrl(item.backdrop_path, 'original')}
              alt={isActive ? title : ''}
              aria-hidden={!isActive}
              className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 ${
                isActive && isLoaded ? 'opacity-100' : 'opacity-0'
              } ${reducedMotion ? '' : 'animate-ken-burns'}`}
              onLoad={() => markLoaded(item.id)}
            />
          );
        })}
        {!loadedKeys[activeMedia.id] && <div className="absolute inset-0 shimmer" />}
      </div>

      {/* Signature: a single Body-Scan emerald sweep reads the featured film on
          load + each rotation (keyed on id so it replays). Reduced-motion safe. */}
      {!reducedMotion && (
        <div key={activeMedia.id} className="hero-scan absolute inset-0" aria-hidden="true" />
      )}

      {/* Moody cinematic scrim — a deep left wash + bottom floor command the copy
          and restore the premium darkness; a soft overall veil keeps the mood;
          the far-right film art still breathes a little. */}
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #030203 0%, rgba(3,2,3,0.92) 34%, rgba(3,2,3,0.5) 62%, transparent 92%)" }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, #030203 0%, rgba(3,2,3,0.72) 32%, rgba(3,2,3,0.15) 62%, transparent 90%)" }} />
      <div className="absolute inset-x-0 top-0 h-28" style={{ background: "linear-gradient(180deg, rgba(3,2,3,0.8), transparent)" }} />
      <div className="absolute inset-0 bg-[#030203]/20" />

      {/* Content */}
      <div className="absolute inset-0 flex items-end pb-28 sm:pb-32 lg:pb-40">
        <div className="max-w-[1920px] mx-auto px-6 sm:px-10 lg:px-16 xl:px-24 w-full">
          {/* key on active id so the staggered reveal replays on rotation */}
          <div key={activeMedia.id} className="max-w-2xl space-y-4">
            {/* Title — cinematic Instrument Serif, staggered reveal */}
            <h1 className="font-display text-hero animate-fade-rise">{title}</h1>

            {/* Meta — editorial row: gold rating · year · type, understated */}
            <div className="flex items-center gap-3 text-sm animate-fade-rise-1 text-gray-300">
              {rating && (
                <span className="inline-flex items-center gap-1.5 font-semibold text-white">
                  <svg className="w-4 h-4" style={{ color: 'var(--color-gold)' }} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  {rating}
                </span>
              )}
              {rating && year && <span className="text-white/25" aria-hidden>•</span>}
              {year && <span>{year}</span>}
              {year && <span className="text-white/25" aria-hidden>•</span>}
              <span className="uppercase tracking-[0.18em] text-xs text-white/70">
                {mediaType === 'movie' ? 'Film' : 'Series'}
              </span>
            </div>

            {/* Body Scan signal — substrate-anchor visible at the hero */}
            {scanSignal && (
              <div className="flex flex-wrap items-center gap-3 max-w-xl animate-fade-rise-1">
                <span className="rounded-full border border-emerald-300/40 bg-emerald-300/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-200">
                  Body Scan
                </span>
                <p className="text-emerald-100/90 text-sm sm:text-base italic leading-snug">
                  {scanSignal}
                </p>
              </div>
            )}

            {/* Overview */}
            <p className="text-gray-300 text-sm sm:text-base lg:text-lg line-clamp-3 lg:line-clamp-4 max-w-xl animate-fade-rise-1">
              {overview}
            </p>

            {/* Actions — calm hierarchy: 2 prominent pills + 2 glass icon buttons */}
            <div className="flex flex-wrap items-center gap-3 pt-4 animate-fade-rise-2">
              {/* Play — prominent primary (CineVault red accent), cleaner pill */}
              <button
                onClick={() => navigate(`/watch/${mediaType}/${activeMedia.id}`)}
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-semibold leading-none text-white
                  bg-gradient-to-r from-[#e50914] to-[#b20710]
                  shadow-[0_4px_14px_rgba(229,9,20,0.4)]
                  hover:shadow-[0_6px_20px_rgba(229,9,20,0.6)]
                  hover:scale-105 active:scale-100
                  transition-all duration-200 group"
              >
                <svg className="w-5 h-5 shrink-0 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
                <span>Play</span>
              </button>

              {/* More Info — secondary glass pill (liquid-glass edge-catch) */}
              <MatrixLink
                to={`/${mediaType}/${activeMedia.id}`}
                titleForTransition={title}
                className="liquid-glass inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold leading-none
                  hover:bg-white/10
                  transition-all duration-200"
              >
                <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>More Info</span>
              </MatrixLink>

              {/* My List — icon-only round glass button (plus / check swap) */}
              <button
                onClick={handleWatchlist}
                aria-label={inWatchlist ? 'In My List' : 'Add to My List'}
                aria-pressed={inWatchlist}
                className="liquid-glass flex items-center justify-center w-12 h-12 rounded-full
                  hover:bg-white/10 transition-all duration-200"
              >
                {inWatchlist ? (
                  <svg className="w-5 h-5 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                )}
              </button>

              {/* Trailer — icon-only round glass button (gated on trailerKey) */}
              {trailerKey && (
                <button
                  onClick={() => setTrailerKey(trailerKey)}
                  aria-label="Watch Trailer"
                  className="liquid-glass flex items-center justify-center w-12 h-12 rounded-full
                    hover:bg-white/10 transition-all duration-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Featured poster (xl+). Framed as intentional art: ring + layered
          shadow + emerald body-scan glow + slow float-bob, so it reads as a
          grounded centerpiece rather than an orphaned floating card. */}
      <div className="hidden xl:block absolute right-16 2xl:right-24 bottom-20 w-56 2xl:w-64 z-[1] animate-slide-in-right">
        <div
          className="animate-float-bob relative rounded-2xl overflow-hidden ring-1 ring-white/15
            shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9),0_0_40px_-16px_rgba(110,231,183,0.18)]"
        >
          <img
            key={activeMedia.id}
            src={getImageUrl(activeMedia.poster_path, 'w500')}
            alt={title}
            className="w-full h-auto block"
          />
          {/* Subtle sheen — top light catch + faint bottom seat */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-white/[0.06]" />
        </div>
      </div>
    </div>
  );
}
